import {
  ColorPaletteName,
  generateUniqueID,
  getReviewQueueFuzzyDueTimestampThreshold,
  Task,
  TaskProvenance,
} from "@withorbit/core";

// ============================================================================
// Constants
// ============================================================================

export const ORPHANS_COLLECTION_ID = "orphans";
export const ORPHANS_COLLECTION_TITLE = "Orphans";

// ============================================================================
// Types
// ============================================================================

export type CollectionID = string;

export interface CollectionSummary {
  id: CollectionID;
  title: string;
  colorPaletteName: ColorPaletteName | null;
  totalPromptCount: number;
  duePromptCount: number;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the collection ID for a task based on its provenance.
 * Tasks without provenance belong to the Orphans collection.
 */
export function getCollectionIDForTask(task: Task): CollectionID {
  if (task.provenance?.identifier) {
    return task.provenance.identifier;
  }
  return ORPHANS_COLLECTION_ID;
}

/**
 * Gets the collection title for a task based on its provenance.
 * Tasks without provenance get the Orphans title.
 */
export function getCollectionTitleForTask(task: Task): string {
  if (task.provenance?.title) {
    return task.provenance.title;
  }
  return ORPHANS_COLLECTION_TITLE;
}

/**
 * Checks if a task is due for review based on the fuzzy threshold.
 */
export function isTaskDue(task: Task, thresholdMillis?: number): boolean {
  const threshold = thresholdMillis ?? getReviewQueueFuzzyDueTimestampThreshold();
  const componentStates = Object.values(task.componentStates);
  return componentStates.some(
    (state) => state.dueTimestampMillis <= threshold,
  );
}

/**
 * Creates a provenance object for the Orphans collection.
 */
export function makeOrphansProvenance(
  colorPaletteName?: ColorPaletteName,
): TaskProvenance {
  return {
    identifier: ORPHANS_COLLECTION_ID,
    title: ORPHANS_COLLECTION_TITLE,
    ...(colorPaletteName ? { colorPaletteName } : {}),
  };
}

/**
 * Creates a new collection provenance with a unique ID.
 */
export function makeNewCollectionProvenance(
  title: string,
  colorPaletteName?: ColorPaletteName,
): TaskProvenance {
  return {
    identifier: generateUniqueID(),
    title,
    ...(colorPaletteName ? { colorPaletteName } : {}),
  };
}

/**
 * Groups tasks by their collection ID.
 */
export function groupTasksByCollection(
  tasks: Task[],
): Map<CollectionID, Task[]> {
  const groups = new Map<CollectionID, Task[]>();

  for (const task of tasks) {
    const collectionID = getCollectionIDForTask(task);
    const existing = groups.get(collectionID);
    if (existing) {
      existing.push(task);
    } else {
      groups.set(collectionID, [task]);
    }
  }

  return groups;
}

/**
 * Builds collection summaries from a list of tasks.
 */
export function buildCollectionSummaries(tasks: Task[]): CollectionSummary[] {
  console.log("[buildCollectionSummaries] Building from", tasks.length, "tasks");
  const groups = groupTasksByCollection(tasks);
  console.log("[buildCollectionSummaries] Grouped into", groups.size, "collections");
  for (const [id, collTasks] of groups) {
    console.log(`[buildCollectionSummaries] Collection "${id}":`, collTasks.length, "tasks");
  }
  const thresholdMillis = getReviewQueueFuzzyDueTimestampThreshold();
  const summaries: CollectionSummary[] = [];

  for (const [collectionID, collectionTasks] of groups) {
    // Find the most common title in the group
    let title = ORPHANS_COLLECTION_TITLE;
    let colorPaletteName: ColorPaletteName | null = null;

    if (collectionID !== ORPHANS_COLLECTION_ID) {
      // Count title occurrences
      const titleCounts = new Map<string, number>();
      for (const task of collectionTasks) {
        const taskTitle = task.provenance?.title;
        if (taskTitle) {
          titleCounts.set(taskTitle, (titleCounts.get(taskTitle) || 0) + 1);
        }
        // Pick first non-null color
        if (!colorPaletteName && task.provenance?.colorPaletteName) {
          colorPaletteName = task.provenance.colorPaletteName;
        }
      }

      // Find most common title
      let maxCount = 0;
      for (const [t, count] of titleCounts) {
        if (count > maxCount) {
          maxCount = count;
          title = t;
        }
      }

      // Fallback to identifier if no title found
      if (maxCount === 0) {
        title = collectionID;
      }
    }

    // Count due prompts
    let duePromptCount = 0;
    for (const task of collectionTasks) {
      if (isTaskDue(task, thresholdMillis)) {
        duePromptCount++;
      }
    }

    summaries.push({
      id: collectionID,
      title,
      colorPaletteName,
      totalPromptCount: collectionTasks.length,
      duePromptCount,
    });
  }

  // Sort: Orphans last, then by total count descending
  summaries.sort((a, b) => {
    if (a.id === ORPHANS_COLLECTION_ID) return 1;
    if (b.id === ORPHANS_COLLECTION_ID) return -1;
    return b.totalPromptCount - a.totalPromptCount;
  });

  return summaries;
}
